import { Badge, Button, cn } from '@provenance/design';
import type { SupportingStampPlacement } from '@provenance/shared';
import { Crosshair, RotateCcw } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * CLICK-TO-PLACE — supporting document stamping (spec §7.6.3)
 *
 * The certificate itself has saved coordinates, because every ChAFTA form is the same
 * shape. A supporting document is whatever the exporter attached: an A4 invoice, a
 * Letter-size packing list, a landscape scan, a phone photo pasted into a page. There is
 * no coordinate that is right for all of them, which is why this is a click rather than
 * a configuration.
 *
 * THE COORDINATE CONVERSION IS THE WHOLE JOB, and it is where this goes wrong:
 *
 *   The browser gives a click in CSS pixels from the TOP-left of the rendered page.
 *   pdf-lib places marks in points from the BOTTOM-left, and the stamp's own anchor is
 *   its bottom-left corner too.
 *
 * So: scale by the page's true point dimensions, flip the Y axis, and subtract the
 * stamp's height so that the marker the operator sees centred under their cursor is
 * where the stamp actually lands. Getting the flip wrong produces a stamp mirrored to the
 * opposite edge, which looks like a data problem and is not.
 *
 * The chamber's configured default position pre-fills the marker, so the common case is
 * confirm-and-move-on rather than a click (§7.6.3 asks for exactly that).
 * ════════════════════════════════════════════════════════════════════════════
 */

/** Matches SupportingDocumentStampConfig's default width. */
const STAMP_WIDTH_PT = 150;
/** The seeded supporting stamp is a wide, short strip; a fair default aspect. */
const STAMP_ASPECT = 51 / 562;

export function StampPlacer({
  document,
  placement,
  onChange,
}: {
  document: {
    id: string;
    filename: string;
    categoryLabel: string;
    fileUrl: string | null;
    pages?: Array<{ width: number; height: number }> | null;
  };
  placement: SupportingStampPlacement | undefined;
  onChange: (placement: SupportingStampPlacement | undefined) => void;
}) {
  const [page, setPage] = useState(1);
  const surfaceRef = useRef<HTMLButtonElement>(null);

  const pages = document.pages ?? [];
  const size = pages[page - 1] ?? { width: 595.28, height: 841.89 };
  const stampHeightPt = STAMP_WIDTH_PT * STAMP_ASPECT;

  const place = useCallback(
    (clientX: number, clientY: number) => {
      const surface = surfaceRef.current;
      if (!surface) return;
      const rect = surface.getBoundingClientRect();

      // Where the cursor is, as a fraction of the rendered page.
      const fx = (clientX - rect.left) / rect.width;
      const fy = (clientY - rect.top) / rect.height;

      // The operator expects the stamp centred on where they clicked, so offset by half
      // its size — then convert to pdf-lib's bottom-left origin.
      const xPt = fx * size.width - STAMP_WIDTH_PT / 2;
      const yPtFromTop = fy * size.height - stampHeightPt / 2;
      const yPt = size.height - yPtFromTop - stampHeightPt;

      onChange({
        supportingDocumentId: document.id,
        page,
        x: Math.round(Math.min(Math.max(xPt, 0), Math.max(0, size.width - STAMP_WIDTH_PT))),
        y: Math.round(Math.min(Math.max(yPt, 0), Math.max(0, size.height - stampHeightPt))),
        width: STAMP_WIDTH_PT,
      });
    },
    [document.id, onChange, page, size.height, size.width, stampHeightPt],
  );

  // Marker position for rendering: back from PDF points to a top-left percentage.
  const marker =
    placement && placement.page === page
      ? {
          left: `${(placement.x / size.width) * 100}%`,
          top: `${((size.height - placement.y - stampHeightPt) / size.height) * 100}%`,
          width: `${(STAMP_WIDTH_PT / size.width) * 100}%`,
          height: `${(stampHeightPt / size.height) * 100}%`,
        }
      : null;

  return (
    <div className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card-alt)] p-3">
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate [font-size:13px] font-medium">{document.filename}</p>
          <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
            {document.categoryLabel}
            {pages.length > 1 && ` · ${pages.length} pages`}
          </p>
        </div>

        {pages.length > 1 && (
          <div className="flex items-center gap-1">
            {pages.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index + 1)}
                className={cn(
                  'size-6 rounded-[var(--pv-radius-xs)] [font-size:11px] font-semibold',
                  page === index + 1
                    ? 'bg-[var(--pv-brand)] text-[var(--pv-brand-on)]'
                    : 'bg-white text-[var(--pv-muted)] hover:text-[var(--pv-ink)]',
                )}
                aria-label={`Page ${index + 1}`}
                aria-current={page === index + 1}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}

        {placement ? (
          <Badge tone="ok" size="sm" dot>
            Placed
          </Badge>
        ) : (
          <Badge tone="clay" size="sm">
            Chamber default
          </Badge>
        )}

        {placement && (
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<RotateCcw className="size-3" />}
            onClick={() => onChange(undefined)}
          >
            Reset
          </Button>
        )}
      </div>

      {/* A representation of the page, not a render of it: an embedded PDF viewer
          swallows clicks, which is precisely what this needs to capture. The point is
          WHERE on the page, and the aspect ratio carries that faithfully. */}
      <button
        ref={surfaceRef}
        type="button"
        onClick={(e) => place(e.clientX, e.clientY)}
        onKeyDown={(e) => {
          // Keyboard placement: arrows nudge by 5pt from the centre, so this is usable
          // without a mouse rather than mouse-only.
          if (!e.key.startsWith('Arrow')) return;
          e.preventDefault();
          const current = placement ?? {
            supportingDocumentId: document.id,
            page,
            x: size.width - STAMP_WIDTH_PT - 24,
            y: 24,
            width: STAMP_WIDTH_PT,
          };
          const step = e.shiftKey ? 20 : 5;
          const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
          const dy = e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0;
          onChange({
            ...current,
            page,
            x: Math.min(Math.max(current.x + dx, 0), Math.max(0, size.width - STAMP_WIDTH_PT)),
            y: Math.min(Math.max(current.y + dy, 0), Math.max(0, size.height - stampHeightPt)),
          });
        }}
        aria-label={`Click to place the stamp on page ${page} of ${document.filename}. Arrow keys nudge it.`}
        // Height-capped, width derived from the aspect ratio. At full dialog width an A4
        // page renders ~1160px tall, which buries the rest of the dialog and makes the
        // operator scroll past the thing they came here to do. Constraining the height
        // and centring keeps the whole page — and the dialog's actions — in view.
        className="relative mx-auto block max-h-[340px] cursor-crosshair overflow-hidden rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-white"
        style={{ aspectRatio: `${size.width} / ${size.height}`, height: 340 }}
      >
        {/* Ruled lines standing in for the document's content, so the surface reads as a
            page rather than an empty box. */}
        <span aria-hidden className="pointer-events-none absolute inset-0 p-[8%]">
          {Array.from({ length: 14 }, (_, i) => (
            <span
              key={i}
              className="mb-[3.6%] block rounded-full bg-[var(--pv-line)]"
              style={{ height: 1.5, width: `${[92, 78, 88, 64, 84, 70][i % 6]}%` }}
            />
          ))}
        </span>

        {marker && (
          <span
            aria-hidden
            className="pv-seal-ring pointer-events-none absolute grid place-items-center rounded-[3px] border-2 border-[var(--pv-leaf-deep)] bg-[var(--pv-leaf-soft)]/85"
            style={marker}
          >
            <Crosshair className="size-3 text-[var(--pv-leaf-deep)]" />
          </span>
        )}

        {!marker && (
          <span className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="rounded-[var(--pv-radius-sm)] bg-[var(--pv-ink)]/80 px-2.5 py-1 [font-size:11px] font-medium text-[var(--pv-on-ink)]">
              Click where the stamp should go
            </span>
          </span>
        )}
      </button>

      <p className="mt-2 [font-size:var(--pv-text-2xs)] leading-snug text-[var(--pv-faint)]">
        {placement
          ? `Placed at ${placement.x}, ${placement.y} points from the bottom-left of page ${placement.page}. Arrow keys nudge by 5pt, Shift for 20.`
          : "Leave it and the chamber's configured default position is used."}
      </p>
    </div>
  );
}
